/**
 * Formata uma data no formato ISO local (sem converter para UTC).
 *
 * O problema: `new Date().toISOString()` converte para UTC, o que
 * causa diferença de horas (ex: 14:22 local → 17:22 UTC em Asunción -03).
 * O backend espera horário local, não UTC.
 *
 * @param date Data a formatar (padrão: agora)
 * @returns String no formato "YYYY-MM-DDTHH:mm:ss"
 */
export function toLocalISOString(date: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

/**
 * Formata apenas a parte da data no formato ISO local (YYYY-MM-DD).
 *
 * @param date Data a formatar
 * @returns String no formato "YYYY-MM-DD"
 */
export function toLocalDateString(date: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/**
 * Converte uma string ISO local "YYYY-MM-DDTHH:mm:ss" (sem timezone) em Date local.
 *
 * Inversa de `toLocalISOString`. Aceita variacoes sem segundos
 * ("YYYY-MM-DDTHH:mm") e apenas data ("YYYY-MM-DD"). Retorna null para
 * entrada vazia/invalida.
 *
 * NAO usa `new Date(string)` direto, pois esse construtor interpreta o formato
 * ISO sem offset como UTC, deslocando o horario (ex.: em Asuncion -03, "14:00"
 * viraria "11:00" local). O parse manual garante interpretacao LOCAL, simetrica
 * a `toLocalISOString`.
 *
 * @param value String ISO local (ou null/undefined)
 * @returns Date local correspondente ou null
 */
export function fromLocalISOString(value: string | null | undefined): Date | null {
  if (!value || typeof value !== 'string' || value.trim().length === 0) {
    return null;
  }

  const [datePart, timePart] = value.trim().split('T');
  const dateSegments = datePart.split('-');
  if (dateSegments.length !== 3) {
    return null;
  }

  const ano = Number(dateSegments[0]);
  const mes = Number(dateSegments[1]);
  const dia = Number(dateSegments[2]);

  let hora = 0;
  let minuto = 0;
  let segundo = 0;
  if (timePart) {
    const timeSegments = timePart.split(':');
    hora = Number(timeSegments[0] ?? 0);
    minuto = Number(timeSegments[1] ?? 0);
    // Segundos podem vir com fracoes (ex.: "59.000") — usa apenas a parte inteira.
    segundo = Number((timeSegments[2] ?? '0').split('.')[0]);
  }

  if ([ano, mes, dia, hora, minuto, segundo].some((n) => Number.isNaN(n))) {
    return null;
  }

  return new Date(ano, mes - 1, dia, hora, minuto, segundo);
}
